import { Search, SettingsInputComponent } from '@mui/icons-material';
import { Checkbox, FormControlLabel, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { ChangeEvent, RefObject, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { RegistrationFieldFilter } from '../../../../../interfaces/interfaces';
import {
  setMessagingOnlineFiltering,
  setMessagingRegistrationFilters,
  setMessagingShowHosts,
  setMessagingSortByName,
} from '../../../../../reduxToolkit/slices/commonSlice';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import AttendeesFilters from './AttendeeFilters/AttendeesFilters';
interface MessagingSearchFieldInterface {
  searchInputRef: RefObject<HTMLInputElement>;
  isLoading?: boolean;
  handleSearch: (value: string) => void;
  handleFilters: (registrationFilters: RegistrationFieldFilter[]) => void;
}
const MessagingSearchField = ({ searchInputRef, isLoading, handleSearch, handleFilters }: MessagingSearchFieldInterface) => {
  const [showFilters, setShowFilters] = useState(false);
  const [onChangeTimeout, setOnChangeTimeout] = useState<ReturnType<typeof setTimeout>>();

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const searchRef = useRef<HTMLInputElement>(null);

  const event = useSelector(({ common }) => common.event);
  const messagingOnlineFiltering = useSelector(({ common }) => common.messagingOnlineFiltering);
  const messagingRegistrationFilters = useSelector(({ common }) => common.messagingRegistrationFilters);
  const messagingShowHosts = useSelector(({ common }) => common.messagingShowHosts);
  const messagingSortByName = useSelector(({ common }) => common.messagingSortByName);

  const handleOnlineFilterChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    localStorage.setItem('messagingOnlineFiltering', String(checked));
    dispatch(setMessagingOnlineFiltering(checked));
  };

  const handleRegistrationFiltersChange = (registrationFilters: RegistrationFieldFilter[]) => {
    dispatch(setMessagingRegistrationFilters(registrationFilters));
    handleFilters(registrationFilters);
  };
  const handleShowHostsChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    dispatch(setMessagingShowHosts(checked));
  };
  const handleSortByName = () => {
    let _messagingSort: 'ascending' | 'descending' | undefined = undefined;
    if (!messagingSortByName) _messagingSort = 'ascending';
    else if (messagingSortByName === 'ascending') _messagingSort = 'descending';
    dispatch(setMessagingSortByName(_messagingSort));
  };
  useEffect(() => {
    if (!searchInputRef.current) return;
  }, [isLoading]);

  const handleSearchChange = (value: string) => {
    onChangeTimeout && clearTimeout(onChangeTimeout);
    const timeout = setTimeout(() => {
      handleSearch(value);
      onChangeTimeout && clearTimeout(onChangeTimeout);
    }, 500);
    setOnChangeTimeout(timeout);
  };

  return (
    <div className="w-full p-4 shadow-sm">
      <TextField
        fullWidth
        placeholder={t('event:messaging.search')}
        variant="outlined"
        onChange={(e) => handleSearchChange(e.target.value)}
        inputRef={searchInputRef}
        disabled={isLoading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />
      <div className="w-full flex flex-col md:flex-row justify-between">
        <div className="flex flex-col items-start md:w-2/3 ">
          <FormControlLabel
            disabled={isLoading}
            control={<Checkbox defaultChecked={messagingOnlineFiltering} onChange={handleOnlineFilterChange} />}
            label={t('event:messaging.show-online')}
          />
          {event?.settings.showToggleShowAdminsInMessagingCenter && event?.settings.allowMessagingOtherAttendees && (
            <FormControlLabel
              disabled={isLoading}
              control={<Checkbox defaultChecked={messagingShowHosts} onChange={handleShowHostsChange} />}
              label={t('event:messaging.show-hosts')}
            />
          )}
          {/*<Button
            variant="text"
            size="small"
            className={messagingSortByName ? 'text-blue-500' : 'text-gray-400'}
            endIcon={messagingSortByName === 'descending' ? <ArrowDownward /> : <ArrowUpward />}
            onClick={handleSortByName}>
            {t('event:messaging.sort-by-name')}
          </Button>*/}
        </div>
        {event?.settings.allowMessagingRegistrationFilters && (
          <div className="flex w-full flex-row justify-start items-center cursor-pointer text-sm" onClick={() => setShowFilters(!showFilters)}>
            <IconButton disabled={isLoading} size="large">
              <SettingsInputComponent />
            </IconButton>
            <p className="text-gray-700">{t('event:messaging.filter-label')}</p>
          </div>
        )}
      </div>
      {showFilters && <AttendeesFilters messagingRegistrationFilters={messagingRegistrationFilters} onApply={handleRegistrationFiltersChange} />}
    </div>
  );
};
export default MessagingSearchField;
